import './task.scss'

import * as _ from 'lodash'
import { defineComponent } from 'vue'
import { combineLatest, timer } from 'rxjs'
import { map } from 'rxjs/operators'
import {
    faAngleDown,
    faAngleRight,
    faCircleNotch,
    faExclamationTriangle,
    faCheck,
} from '@fortawesome/free-solid-svg-icons'
import { BModal } from 'bootstrap-vue'

import { Item, Movement, MovementTypes, ThesaurusTerm, Featured, Task, TaskLifecycleStates, getTranslatedString } from '@skinsoft/types'
import { TrackingRoutes } from '@skinsoft/const'
import { itemSelectors, taskSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'
import {
    itemSorters,
    MovementDataStore,
} from '@skinsoft/entity'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { TaskFeaturedCard } from '@skinsoft/entity-ui'
import { ItemCard, PullToRight } from '@skinsoft/ui'
import {
    CREATE_COMPLEX_MOVEMENT_FROM_TASK_TOPIC,
    CREATE_SIMPLE_MOVEMENT_FROM_TASK_TOPIC,
    REJECT_TASK_TOPIC,
    START_TASK_TOPIC,
    TERMINATE_TASK_TOPIC,
} from '../../../busTopics'
import { CommentEditor } from '../../component/comment-editor'
import { AdvanceTaskModal } from '../../component/advance-task-modal'

export const TaskPage = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sCommentEditor: CommentEditor,
        sItemCard: ItemCard,
        sPullToRight: PullToRight,
        sTaskFeaturedCard: TaskFeaturedCard,
        advanceTaskModal: AdvanceTaskModal,
    },
    template: require('./task.html'),
    data() {
        return {
            angleDownIcon: faAngleDown,
            angleRightIcon: faAngleRight,
            exclamationTriangleIcon: faExclamationTriangle,
            itemSelectors: itemSelectors,
            spinnerIcon: faCircleNotch,
            checkIcon: faCheck,
            targetLifecycle: TaskLifecycleStates.IN_PROGRESS,

            hasPendingMovement: false,
            items: [null, null, null] as any[],
            task: null as any as Task,
            thesaurusTerm: [] as ThesaurusTerm[],
            showCollapse: false,
            operatorCommentPersisting: false,
            hasUnsavedComment: false,

            movementType: null as MovementTypes | null,
        }
    },
    computed: {
        description(): string | null {
            if(this.thesaurusTerm && this.thesaurusTerm.length > 0) {
                let description = ''
                for(const thesaurusTerm of this.thesaurusTerm) {
                    const preferredTerm = getTranslatedString(
                        thesaurusTermSelectors.preferredTerm(thesaurusTerm),
                        this.getAppLocale()
                    )
                    description += preferredTerm ? preferredTerm + ', ' : ''
                }
                return description.charAt(description.length - 2) === ',' ? description.slice(0, -2) : description
            }
            return null
        },

        precisionRequest(): ReturnType<typeof taskSelectors.precisionRequest> {
            return taskSelectors.precisionRequest(this.task)
        },

        operatorComment(): ReturnType<typeof taskSelectors.operatorComment> {
            return taskSelectors.operatorComment(this.task, this.getAppLocale())
        },

        state(): ReturnType<typeof taskSelectors.state> {
            return taskSelectors.state(this.task)
        },
    },
    mounted() {
        this.subscribeToBus(CREATE_SIMPLE_MOVEMENT_FROM_TASK_TOPIC, () => {
            this.movementType = MovementTypes.SIMPLE
            ;(this.$refs.createMovementModal as InstanceType<typeof BModal>).show()

        })
        this.subscribeToBus(CREATE_COMPLEX_MOVEMENT_FROM_TASK_TOPIC, () => {
            this.movementType = MovementTypes.COMPLEX
            ;(this.$refs.createMovementModal as InstanceType<typeof BModal>).show()

        })
        this.subscribeToBus(REJECT_TASK_TOPIC, () => this.rejectTask())
        this.subscribeToBus(START_TASK_TOPIC, () => this.startTask())
        this.subscribeToBus(TERMINATE_TASK_TOPIC, () => this.terminateTask())
        this.subscribeToEntity()
    },
    methods: {
        rejectTask() {
            this.targetLifecycle = TaskLifecycleStates.PLANNED
            if (this.$refs.advanceTaskModal) {
                (this.$refs.advanceTaskModal as InstanceType<typeof AdvanceTaskModal>).open()
            }
        },

        startTask() {
            this.targetLifecycle = TaskLifecycleStates.IN_PROGRESS
            if (this.$refs.advanceTaskModal) {
                (this.$refs.advanceTaskModal as InstanceType<typeof AdvanceTaskModal>).open()
            }
        },

        terminateTask() {
            this.targetLifecycle = TaskLifecycleStates.FINALIZED
            if (this.$refs.advanceTaskModal) {
                (this.$refs.advanceTaskModal as InstanceType<typeof AdvanceTaskModal>).open()
            }
        },

        subscribeToEntity() {
            this.subscribeTo(
                this.$taskDataStore.fetchOne(this.$route.params.id as string),
                (task) => {
                    this.task = task
                },
                'task',
            )
        },

        subscribeToItems() {
            const linkableItems = taskSelectors.linkableItems(this.task)

            if (linkableItems.length === 0) {
                this.items = []
                return
            }

            this.subscribeTo(
                combineLatest(
                    ...linkableItems
                        .filter(item => item.linkableItem.linkableItem)
                        .map((item) => {
                            return this.$itemDataStore.fetchIfHasAccess(item.linkableItem.linkableItem!)
                        }),
                ).pipe(
                    map(items => items.filter(_.identity))
                ),
                (items) => {
                    this.items = items.sort(itemSorters.byRealTimeLocation) as Item[]
                },
                'items',
            )
        },

        subscribeToThesaurusTerm() {
            const ids = taskSelectors.associatedTasks(this.task)

            if (!ids) {
                this.thesaurusTerm = []
                return
            }

            this.subscribeTo(
                this.$thesaurusTermDataStore.fetchMany(ids),
                (thesaurusTerms) => {
                    if(thesaurusTerms) {
                        if(thesaurusTerms.allData && thesaurusTerms.allData.length > 0) {
                            this.thesaurusTerm = thesaurusTerms.allData
                            return
                        }
                        else if(thesaurusTerms.results && thesaurusTerms.results.length > 0) {
                            this.thesaurusTerm = thesaurusTerms.results
                            return
                        }
                    }
                    this.thesaurusTerm = []
                },
                'thesaurusTerm',
            )
        },

        onRouteChange() {
            // to hide the reset of the puller, we reset the state of this page to make it look like the same as the mockup view
            this.items = [null, null, null] as any[]
            ;(this.$refs.puller as InstanceType<typeof PullToRight>).reset()

            this.unsubscribeFrom('task')
            this.subscribeToEntity()
        },

        onEntityChange() {
            this.unsubscribeFrom('items')
            this.subscribeToItems()
            this.unsubscribeFrom('thesaurusTerm')
            this.subscribeToThesaurusTerm()
        },

        onCommentEditorChange(operatorComment: string) {
            _.set(
                this.task,
                `taskSchema.operatorComment.translatedString.${this.getAppLocale()}`,
                operatorComment,
            )
            this.hasUnsavedComment = true
        },

        async onCommentEditorSave() {
            this.operatorCommentPersisting = true
            try {
                await this.$taskDataStore.updateOperatorComment(
                    this.task.id,
                    this.operatorComment,
                    this.getAppLocale(),
                )
                this.hasUnsavedComment = false
            } finally {
                this.operatorCommentPersisting = false
            }
        },

        onPulled() {
            // the user has reach pull-right non return point, we must transition to the next entity
            const next = this.$route.query.next as any
            const id = (next as string[]).shift() as string

            requestAnimationFrame(() =>
                this.$router.push({
                    name: this.$route.name || '',
                    params: { id },
                    query: (next.length > 0 ? { next } : {}) as Record<string, string>,
                }),
            )
        },

        toggleCollapse([_, event]: [Featured, MouseEvent]) {
            if (event.target && (event.target as HTMLElement).id === 'operatorComment') {
                return
            }

            this.showCollapse = !this.showCollapse
        },

        async createMovementFromTask(
            movementType: MovementTypes.SIMPLE | MovementTypes.COMPLEX = MovementTypes.SIMPLE,
        ) {
            this.movementType = null
            this.hasPendingMovement = true

            let movement: Movement
            try {
                movement = await (this.$movementDataStore as MovementDataStore).createMovement(
                    movementType,
                    this.task.id,
                )
            } finally {
                this.hasPendingMovement = false
            }

            if (movement) {
                this.$router.push({
                    name: TrackingRoutes.MOVEMENT,
                    params: { id: movement.id },
                })
            }
        },

        refreshTask() {
            this.unsubscribeFrom('task')
            timer(1000).subscribe(this.subscribeToEntity)
        },
    },
    watch: {
        '$route': {
            handler: 'onRouteChange',
            deep: true,
        },
        task: {
            handler: 'onEntityChange',
            deep: true,
        },
    },
})
